import { ChakraProvider, extendTheme, SimpleGrid } from '@chakra-ui/react';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/800.css';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import {
    EducationInput,
    ExperienceInput,
    useMeQuery,
    useUpdateUserMutation,
    useUpsertEducationMutation,
    useUpsertProfileMutation
} from '../../generated/graphql';
import './CreateProfile.css';
import MultiStepForm from './MultiStepForm';
import Authorization from './steps/Authorization';
import Careers from './steps/Careers';
import Compensation from './steps/Compensation';
import Demographic from './steps/Demographic';
import Experience from './steps/Experience';
import Locations from './steps/Locations';
import Pronouns from './steps/Pronouns';
import Roles from './steps/Roles';
import School from './steps/School';
import ShortBio from './steps/ShortBio';
import Skills from './steps/Skills';
import Tools from './steps/Tools';
import Website from './steps/Website';
import Welcome from './steps/Welcome';

const theme = extendTheme({
    fonts: {
        heading: 'Manrope',
        body: 'Manrope'
    }
});

export default function CreateProfile(props: any) {
    const { data: userData, loading: isLoading } = useMeQuery({
        variables: {}
    });
    const [user, setUser] = useState(userData?.me);
    const [slug, setSlug] = useState('');
    const [shortBio, setShortBio] = useState('');
    const [checkedLocations, setCheckedLocations] = useState([] as string[]);
    const [choices, setChoices] = useState([] as string[]);
    const [compensation, setCompensation] = useState([] as string[]);
    const [skillsChoices, setSkillsChoices] = useState([] as string[]);
    const [tools, setTools] = useState([] as string[]);
    const [experiences, setExperiences] = useState([] as ExperienceInput[]);

    const formik = useFormik({
        initialValues: {
            firstName: user?.first_name,
            lastName: user?.last_name,
            email: user?.email,
            phone: user?.phoneNumber ?? '',
            pronouns: user?.pronouns ?? '',
            shortBio: user?.profile?.small_description ?? '',
            university: user?.education[0]?.school ?? '',
            major: user?.education[0]?.major ?? '',
            classOf: user?.education[0]?.graduation_date ?? '',
            careers: user?.profile?.industries ?? '',
            compensation: user?.profile?.compensation ?? '',
            locations: user?.profile?.locations ?? '',
            skills: user?.profile?.strengths ?? '',
            roles:
                user?.profile?.internship && user?.profile?.full_time
                    ? 'Both'
                    : user?.profile?.internship
                    ? 'Internships'
                    : 'Jobs',
            'time-commitment':
                user?.profile?.internship && user?.profile?.full_time
                    ? 'Both'
                    : user?.profile?.internship
                    ? 'Part-Time'
                    : 'Full-Time',
            'start-date': user?.profile?.internship_seasons
                ? user?.profile?.internship_seasons[0]
                : '',
            tools: user?.profile?.tools ?? '',
            website: user?.profile?.portfolio_link ?? '',
            authorization: user?.profile?.is_legal ? 'Yes' : 'No',
            needVisa: user?.profile?.needs_visa ? 'Yes' : 'No',
            demographics: user?.ethnicity ?? '',
            experiences: user?.profile?.experience ?? []
        },
        onSubmit: (values) => {},
        enableReinitialize: true
    });

    useEffect(() => {
        setUser(userData?.me);
        let initialExp = [] as ExperienceInput[];
        if (userData?.me?.profile?.experience) {
            initialExp = userData?.me?.profile?.experience as ExperienceInput[];
        }
        setShortBio(userData?.me?.profile?.small_description ?? '');
        setCheckedLocations((userData?.me?.profile?.locations as string[]) ?? []);
        setChoices((userData?.me?.profile?.industries as string[]) ?? []);
        setCompensation((userData?.me?.profile?.compensation as string[]) ?? []);
        setSkillsChoices((userData?.me?.profile?.strengths as string[]) ?? []);
        setTools((userData?.me?.profile?.tools as string[]) ?? []);
        setExperiences(initialExp);
    }, [userData]);

    const [formError, setFormError] = useState(false);

    const handleInputChange = (e: any) => {
        let inputValue = e.target.value;
        setShortBio(inputValue);
    };

    const handleToolsChange = (e: any) => {
        let toolsCopy = [...tools];
        if (toolsCopy.includes(e.target.value)) {
            toolsCopy.splice(toolsCopy.indexOf(e.target.value), 1);
            setTools([...toolsCopy]);
        } else setTools([...toolsCopy, e.target.value]);
    };

    const handleSkillCheckboxChange = (e: any) => {
        let skillsChoicesCopy = [...skillsChoices];
        if (skillsChoicesCopy.length < 5) {
            if (skillsChoicesCopy.includes('')) {
                skillsChoicesCopy.pop();
            }
            if (skillsChoicesCopy.includes(e.target.value)) {
                skillsChoicesCopy.splice(skillsChoicesCopy.indexOf(e.target.value), 1);
                setSkillsChoices([...skillsChoicesCopy]);
            } else setSkillsChoices([...skillsChoicesCopy, e.target.value]);
        } else {
            if (skillsChoices.includes(e.target.value)) {
                skillsChoicesCopy.splice(skillsChoicesCopy.indexOf(e.target.value), 1);
                setSkillsChoices([...skillsChoicesCopy]);
            }
        }
    };

    const handleLocationsCheckboxChange = (e: any) => {
        let checkLocationsCopy = [...checkedLocations];
        if (checkLocationsCopy.includes('')) {
            checkLocationsCopy.pop();
        }
        if (checkLocationsCopy.includes(e.target.value)) {
            checkLocationsCopy.splice(checkLocationsCopy.indexOf(e.target.value), 1);
            setCheckedLocations([...checkLocationsCopy]);
        } else setCheckedLocations([...checkLocationsCopy, e.target.value]);
    };

    const navigate = useNavigate();
    const handleFormSubmit = async (values: any) => {
        navigate(`/profile/${slug}`);
    };

    const [upsertProfile] = useUpsertProfileMutation();
    const [updateUserMutation] = useUpdateUserMutation();
    const onWelcomeSubmit = async (event: any) => {
        const userResponse = await updateUserMutation({
            variables: {
                input: {
                    first_name: event.firstName,
                    last_name: event.lastName,
                    phoneNumber: event.phone
                }
            }
        });

        setSlug(userResponse.data?.updateUser?.me?.profile?.slug || '');
    };

    const onPronounSubmit = async (event: any) => {
        await updateUserMutation({
            variables: {
                input: {
                    pronouns: event.pronouns
                }
            }
        });
    };

    const onBioSubmit = async (event: any) => {
        await upsertProfile({
            variables: {
                input: {
                    small_description: event.shortBio
                }
            }
        });
    };

    const [upsertEducation] = useUpsertEducationMutation();
    const onEducationSubmit = async (event: any) => {
        const education: EducationInput = {};

        if (event.university) {
            education['school'] = event.university;
        }

        if (event.major) {
            education['major'] = event.major;
        }

        if (event.classOf) {
            education['graduation_date'] = event.classOf;
        }
        if (event.classOf === "I'm not in school") {
            education['graduation_date'] = '';
            education['major'] = '';
            education['school'] = '';
        }

        await upsertEducation({
            variables: {
                input: education
            }
        });
    };

    const onCareersSubmit = async (event: any) => {
        await upsertProfile({
            variables: {
                input: {
                    industries: choices
                }
            }
        });
    };

    const onCompensationSubmit = async (event: any) => {
        await upsertProfile({
            variables: {
                input: {
                    compensation: event.compensation
                }
            }
        });
    };

    const onLocationsSubmit = async (event: any) => {
        await upsertProfile({
            variables: {
                input: {
                    locations: event.locations
                }
            }
        });
    };

    const onSkillsSubmit = async (event: any) => {
        await upsertProfile({
            variables: {
                input: {
                    strengths: event.skills
                }
            }
        });
    };

    const onToolsSubmit = async (event: any) => {
        await upsertProfile({
            variables: {
                input: {
                    tools: tools
                }
            }
        });
    };

    const onRolesSubmit = async (event: any) => {
        const profile: any = {};
        if (event['roles'] === 'Both') {
            profile.internship = true;
            profile.full_time = true;
        } else if (event['roles'] === 'Internship') {
            profile.internship = true;
            profile.full_time = false;
        } else if (event['roles'] === 'Full-Time') {
            profile.internship = false;
            profile.full_time = true;
        }

        // TODO: make start-date multiselect
        if (event['start-date']) {
            profile.internship_seasons = [event['start-date']];
            profile.actively_looking = true;
        }

        if (event['time-commitment'] === 'Both') {
            profile.availability = ['PART_TIME', 'FULL_TIME'];
        } else if (event['time-commitment'] === 'Part-Time') {
            profile.availability = ['PART_TIME'];
        } else if (event['time-commitment'] === 'Full-Time') {
            profile.availability = ['FULL_TIME'];
        }

       await upsertProfile({
            variables: {
                input: profile
            }
        });
    };

    const onWebsiteSubmit = async (event: any) => {
       await upsertProfile({
            variables: {
                input: {
                    portfolio_link: event.website
                }
            }
        });
    };

    const onDemographicsSubmit = async (event: any) => {
        if (event.demographics) {
           await updateUserMutation({
                variables: {
                    input: {
                        ethnicity: event.demographics
                    }
                }
            });
        }
    };

    const onAuthorizationSubmit = async (event: any) => {
       await upsertProfile({
            variables: {
                input: {
                    is_legal: event.authorization === 'Yes',
                    needs_visa: event.visa === 'Yes'
                }
            }
        });
    };

    const validationSchemaUniversity = yup.object().shape({
        major: yup.string().when('classOf', {
            is: (classOf: string) => classOf !== "I'm not in school",
            then: yup.string().required('Major is required.')
        }),
        classOf: yup.string().required('Please enter a valid year.'),
        university: yup.string().when('classOf', {
            is: (classOf: string) => classOf !== "I'm not in school",
            then: yup.string().required('University is required.')
        })
    });

    const validationSchemaWelcome = yup.object().shape({
        firstName: yup.string().required('First name is required.'),
        lastName: yup.string().required('Last name is required.'),
        email: yup.string().email('').required('Please enter a valid email.'),
        phone: yup
            .number()
            .min(1000000000)
            .max(9999999999)
            .required('Please enter a valid phone number.')
    });

    const validationSchemaPronouns = yup.object().shape({
        pronouns: yup.string().required('Please select your pronouns.')
    });

    const validationSchemaBio = yup.object().shape({
        shortBio: yup
            .string()
            .required('Please enter a short bio.')
            .max(250, 'Bio must be less than 250 characters')
    });

    const validationSchemaCareers = yup.object().shape({
        careers: yup.array().required('').min(1, '')
    });

    const validationSchemaCompensation = yup.object().shape({
        compensation: yup.array().required('').min(1, '')
    });

    const validationSchemaLocation = yup.object().shape({
        locations: yup.array().required('').min(1, '')
    });

    const validationSchemaSkills = yup.object().shape({
        skills: yup.array().required('').min(3, '').max(5, '')
    });

    const validationSchemaRoles = yup.object().shape({
        roles: yup.string().required('Please select a role type.'),
        'time-commitment': yup.string().required('Please select your time commitment.'),
        'start-date': yup.string().required('Please select your start date.')
    });

    const validationSchemaTools = yup.object().shape({
        tools: yup.array().required('').min(1, '')
    });

    const validationSchemaAuthorization = yup.object().shape({
        authorization: yup.string().required('Please select an option.'),
        needVisa: yup.string().required('Please select an option.')
    });

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <ChakraProvider theme={theme}>
            <SimpleGrid>
                <MultiStepForm
                    enableReinitialize={true}
                    initialValues={formik.initialValues}
                    onSubmit={handleFormSubmit}
                >
                    <Welcome
                        user={user}
                        onSubmit={onWelcomeSubmit}
                        validationSchema={validationSchemaWelcome}
                    />
                    <Pronouns
                        onSubmit={onPronounSubmit}
                        validationSchema={validationSchemaPronouns}
                    />
                    <ShortBio
                        onSubmit={onBioSubmit}
                        shortBio={shortBio}
                        handleInputChange={handleInputChange}
                        validationSchema={validationSchemaBio}
                    />
                    <School
                        onSubmit={onEducationSubmit}
                        validationSchema={validationSchemaUniversity}
                    />
                    <Careers
                        onSubmit={onCareersSubmit}
                        choices={choices}
                        setChoices={setChoices}
                        formError={formError}
                        hasOwnNavigation
                        validationSchema={validationSchemaCareers}
                    />
                    <Compensation
                        onSubmit={onCompensationSubmit}
                        formError={formError}
                        compensation={compensation}
                        setCompensation={setCompensation}
                        validationSchema={validationSchemaCompensation}
                    />
                    <Locations
                        onSubmit={onLocationsSubmit}
                        formError={formError}
                        checkedLocations={checkedLocations}
                        handleLocationsCheckboxChange={handleLocationsCheckboxChange}
                        validationSchema={validationSchemaLocation}
                    />
                    <Skills
                        onSubmit={onSkillsSubmit}
                        formError={formError}
                        handleSkillCheckboxChange={handleSkillCheckboxChange}
                        skillsChoices={skillsChoices}
                        validationSchema={validationSchemaSkills}
                    />

                    <Roles
                        onSubmit={onRolesSubmit}
                        formError={formError}
                        validationSchema={validationSchemaRoles}
                    />

                    <Experience experiences={experiences} setExperiences={setExperiences} />
                    
                    <Tools
                        onSubmit={onToolsSubmit}
                        formError={formError}
                        tools={tools}
                        handleToolsChange={handleToolsChange}
                        validationSchema={validationSchemaTools}
                    />
                    {/* <ProfilePhotoUpload /> */}
                    <Website
                        onSubmit={onWebsiteSubmit}
                        formError={formError}
                        hasSkipButton
                        skipbuttonText="Skip for now →"
                    />
                    {/* <ResumeUpload /> */}
                    <Demographic onSubmit={onDemographicsSubmit} formError={formError} hasSkipButton skipbuttonText="Skip for now →"/>
                    <Authorization
                        onSubmit={onAuthorizationSubmit}
                        formError={formError}
                        validationSchema={validationSchemaAuthorization}
                    />
                </MultiStepForm>
            </SimpleGrid>
        </ChakraProvider>
    );
}
